import { useQuery } from '@tanstack/react-query';
import { AudioLines, Loader2, Mic, MicOff, PhoneOff, Wrench } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import type { LiveLine, LiveState } from '@/lib/use-live-vicki';
import { useLiveVicki } from '@/lib/use-live-vicki';
import { cn } from '@/lib/utils';

/** Whether Live VICKI is on for the account (docs/features/front-desk.md#live-vicki). */
export function useLiveVickiOn(accountId: string) {
  const settings = useQuery({ queryKey: qk.liveVicki(accountId), queryFn: () => api.frontDesk.live(accountId), staleTime: 60_000 });
  return !!settings.data?.runtime?.usable;
}

export type LiveVicki = ReturnType<typeof useLiveVicki>;
export { useLiveVicki };

/** The button that starts talking with VICKI, or ends it. */
export function LiveVickiButton({ live, compact, className }: { live: LiveVicki; compact?: boolean; className?: string }) {
  const label = live.active ? t('End talking') : t('Talk with VICKI');
  return (
    <Button
      size={compact ? 'icon' : 'sm'}
      variant={live.active ? 'destructive' : 'default'}
      className={cn(compact ? 'size-10' : 'shrink-0', !live.active && 'bg-gradient-to-r from-primary to-violet-500 text-white hover:opacity-90', className)}
      onClick={() => (live.active ? live.stop() : void live.start())}
      aria-label={label}
      title={label}
    >
      {live.state === 'connecting' ? <Loader2 className="animate-spin" /> : live.active ? <PhoneOff /> : <AudioLines />}
      {!compact && label}
    </Button>
  );
}

const STATE_TEXT: Record<LiveState, string> = {
  idle: '',
  connecting: t('Connecting …'),
  listening: t('Listening'),
  hearing: t('Listening …'),
  speaking: t('VICKI speaks'),
  working: t('VICKI works'),
  error: '',
};

/** How loud a stream is, 0..1, written into a CSS variable of `el` every frame (no re-render). */
function useLevel(stream: MediaStream | null, el: React.RefObject<HTMLElement | null>, name: string) {
  useEffect(() => {
    if (!stream || !el.current || !stream.getAudioTracks().length) return;
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let frame = 0;
    let level = 0;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (const v of data) sum += v;
      level = level * 0.7 + Math.min(1, sum / data.length / 90) * 0.3;
      el.current?.style.setProperty(name, level.toFixed(3));
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(frame);
      void ctx.close();
      el.current?.style.setProperty(name, '0');
    };
  }, [stream, el, name]);
}

/** Her orb: it breathes with her voice and with the person's. */
function Orb({ live }: { live: LiveVicki }) {
  const ref = useRef<HTMLDivElement>(null);
  useLevel(live.streams.voice, ref, '--voice');
  useLevel(live.muted ? null : live.streams.mic, ref, '--mic');
  return (
    <div ref={ref} className="relative flex size-14 shrink-0 items-center justify-center" style={{ '--voice': 0, '--mic': 0 } as React.CSSProperties}>
      <div
        className={cn('absolute inset-0 rounded-full bg-gradient-to-br from-primary to-violet-500 opacity-30 blur-md transition-transform', live.state === 'working' && 'animate-pulse')}
        style={{ transform: 'scale(calc(1 + var(--voice) * 0.7 + var(--mic) * 0.35))' }}
      />
      <div
        className={cn('relative size-11 rounded-full bg-gradient-to-br from-primary to-violet-500 shadow-lg', live.state === 'connecting' && 'animate-pulse')}
        style={{ transform: 'scale(calc(1 + var(--voice) * 0.25))' }}
      />
      {live.state === 'working' && <Wrench className="absolute size-4 animate-spin text-white [animation-duration:3s]" />}
    </div>
  );
}

function Line({ l }: { l: LiveLine }) {
  return (
    <div className={cn('truncate', l.who === 'you' ? 'text-foreground' : l.who === 'tool' ? 'text-xs text-muted-foreground italic' : 'font-medium text-primary')}>
      {l.who === 'you' ? `${t('You')}: ` : l.who === 'vicki' ? 'VICKI: ' : '→ '}
      {l.text}
    </div>
  );
}

/**
 * The voice conversation with VICKI above her chat (docs/features/front-desk.md#live-vicki): her orb, what she does,
 * the last things said, mute and end. What she writes into the chat shows in the chat below.
 */
export function LiveVickiPanel({ live, className }: { live: LiveVicki; className?: string }) {
  if (live.state === 'idle') return null;
  const recent = live.lines.filter((l) => l.text.trim()).slice(-3);
  return (
    <div className={cn('border-b bg-gradient-to-r from-primary/5 to-violet-500/5 px-4 py-3 sm:px-6', className)}>
      <div className="mx-auto flex max-w-3xl items-center gap-3">
        <Orb live={live} />
        <div className="min-w-0 flex-1 text-sm">
          <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {live.state === 'error' ? t('Live VICKI') : live.muted ? t('Muted') : STATE_TEXT[live.state]}
          </div>
          {live.error ? (
            <div className="text-sm text-destructive">{live.error}</div>
          ) : recent.length ? (
            recent.map((l) => <Line key={l.id} l={l} />)
          ) : (
            <div className="text-muted-foreground">{live.state === 'connecting' ? t('VICKI picks up …') : t('Just talk. VICKI writes bigger work into the chat and tells you when it is done.')}</div>
          )}
        </div>
        {live.active ? (
          <>
            <Button size="icon" variant={live.muted ? 'secondary' : 'ghost'} onClick={live.toggleMute} aria-label={live.muted ? t('Unmute') : t('Mute')} title={live.muted ? t('Unmute') : t('Mute')}>
              {live.muted ? <MicOff /> : <Mic />}
            </Button>
            <Button size="icon" variant="destructive" onClick={live.stop} aria-label={t('End talking')} title={t('End talking')}>
              <PhoneOff />
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => void live.start()}>
            {t('Try again')}
          </Button>
        )}
      </div>
    </div>
  );
}
